import React, { useEffect, useRef, useState } from 'react'

import oneTeamImage from '../../assets/images/services/service-people-3.png'
import { ONE_TEAM_DOT_COLORS } from '../../constants/serviceOneTeamContent'

/**
 * “One Team” + “Work Together” blocks for service detail pages.
 *
 * @param {string} titleBefore
 * @param {string} titleHighlight
 * @param {string} imageAlt
 * @param {{ id: string, title: string, body: string, dotCount: number }[]} steps
 * @param {{ titleBefore: string, titleHighlight: string, imageAlt: string, imageSrc: string, columns: { id: string, title: string, body: string, icon: string }[] }} [workTogether]
 * @param {string[]} [dotColors]
 * @param {string} [idBase] — optional stable id prefix for ARIA hooks
 */
const ServiceOneTeam = ({
  titleBefore,
  titleHighlight,
  imageAlt,
  steps,
  workTogether,
  dotColors = ONE_TEAM_DOT_COLORS,
  idBase = 'service-one-team',
}) => {
  const uid = idBase
  const rootRef = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current) return undefined
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true)
      return undefined
    }

    const el = rootRef.current
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          obs.disconnect()
        }
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  if (!steps.length) {
    return null
  }

  const hasWorkTogether =
    workTogether &&
    workTogether.columns &&
    workTogether.columns.length > 0 &&
    workTogether.imageSrc

  const sectionLabelledBy = hasWorkTogether
    ? `${uid}-heading ${uid}-work-heading`
    : `${uid}-heading`

  return (
    <section
      ref={rootRef}
      className={
        'service-one-team' + (visible ? ' service-one-team--visible' : '')
      }
      aria-labelledby={sectionLabelledBy}
    >
      <div className="content container">
        <h2 id={`${uid}-heading`} className="service-one-team__heading">
          {titleBefore}
          <span className="service-one-team__heading-accent">{titleHighlight}</span>
        </h2>
        <div className="service-one-team__grid-wrap">
          <div className="service-one-team__cards">
            {steps.map((step, index) => (
              <article key={step.id} className="service-one-team__card">
                <h3
                  id={`${uid}-step-${step.id}`}
                  className="service-one-team__card-title"
                >
                  {index + 1}. {step.title}
                </h3>
                <div className="service-one-team__dots" aria-hidden="true">
                  {dotColors.slice(0, step.dotCount).map((color, i) => (
                    <span
                      key={i}
                      className="service-one-team__dot"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <p className="service-one-team__card-text">{step.body}</p>
              </article>
            ))}
          </div>
          <figure className="service-one-team__figure">
            <img
              className="service-one-team__image"
              src={oneTeamImage}
              alt={imageAlt}
              width={600}
              height={600}
              loading="lazy"
              decoding="async"
            />
          </figure>
        </div>

        {hasWorkTogether && (
          <div className="service-one-team__work-together">
            <h2
              id={`${uid}-work-heading`}
              className="service-one-team__work-heading"
            >
              {workTogether.titleBefore}
              <span className="service-one-team__heading-accent">
                {workTogether.titleHighlight}
              </span>
            </h2>
            <div
              className="service-one-team__work-columns"
              role="list"
            >
              {workTogether.columns.map(col => (
                <div
                  key={col.id}
                  className="service-one-team__work-col"
                  role="listitem"
                >
                  <div className="service-one-team__work-icon-wrap">
                    <img
                      src={col.icon}
                      alt=""
                      width={72}
                      height={52}
                      decoding="async"
                    />
                  </div>
                  <h3 className="service-one-team__work-col-title">
                    {col.title}
                  </h3>
                  <p className="service-one-team__work-col-text">{col.body}</p>
                </div>
              ))}
            </div>
            <figure className="service-one-team__work-figure">
              <img
                className="service-one-team__work-image"
                src={workTogether.imageSrc}
                alt={workTogether.imageAlt}
                width={1920}
                height={1042}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
        )}
      </div>
    </section>
  )
}

export default ServiceOneTeam
